/*
 *  -----------------------------------------------------------------------
 *    <copyright file="risk-overview-scrollable-grid/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { orderBy } from '@progress/kendo-data-query';
import cn from 'clsx';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
    RiskOverviewColumn,
    RiskOverviewScrollableGridProps,
} from '@components/central-monitoring/study-summary/risk-overview-scrollable-grid/risk-overview-scrollable-grid.interface';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import {
    ExcelExportConfig,
    IActionItemV2,
} from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExportExcelComponentInputColumn } from '@components/core/export-excel';
import { getRiskOverviewGridColumns } from './risk-overview-scrollable-grid.helper';
import { useTranslation } from 'hooks';
import { createRiskOverviewGrid } from 'utils/data-table.service';
import '../../reports/kri-scores-grid.scss';
import { calculateRiskOverviewColumns, calculateRiskOverviewsAndStudySites } from '@utils/cm-review-report';
import { stringDateWithoutTimeFromCurrentUtcDate, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { AllRoutes } from '@utils/router/all-routes';
import NavigationService from '@components-clean/page-nav/navigation.service';

const RiskOverviewScrollableGrid = ({
    studyData,
    config = {},
    sitesStatusesToExclude,
    isExcludeSites = true,
    gridViewOptions,
    siteScores,
    reportData,
    switchMenu,
    inactive,
}: RiskOverviewScrollableGridProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [columns, setColumns] = useState([]);
    const [items, setItems] = useState([]);

    const createGridColumns = (riskOverviewColumns: any[]) =>
        getRiskOverviewGridColumns(config, riskOverviewColumns, t);

    const createGridData = (gridColumns: any[], riskOverviews: any) => {
        let sites = createRiskOverviewGrid(studyData, riskOverviews, sitesStatusesToExclude, gridColumns);

        if (isExcludeSites) sites = sites.filter((item: any) => item.siteToShow);

        return orderBy(sites, [
            { field: 'siteToShow', dir: 'desc' },
            { field: 'score', dir: 'desc' },
        ]);
    };

    const createDataForGrid = (riskOverviewColumns: any[], riskOverviews: any) => {
        const gridColumns = createGridColumns(riskOverviewColumns);
        const gridData = createGridData(gridColumns, riskOverviews);

        return {
            columns: gridColumns,
            data: gridData,
        };
    };

    useEffect(() => {
        if (studyData) {
            if (sitesStatusesToExclude && reportData && siteScores) {
                const riskOverviewColumns = calculateRiskOverviewColumns(config.panels);
                const dataForPreview = calculateRiskOverviewsAndStudySites(
                    studyData.sites,
                    siteScores,
                    false,
                    sitesStatusesToExclude,
                    config.panels,
                    riskOverviewColumns,
                    reportData?.centralMonitoringReviewData?.studySites,
                );
                if (riskOverviewColumns && dataForPreview.riskOverviews?.length) {
                    const result = createDataForGrid(riskOverviewColumns, dataForPreview.riskOverviews);
                    setColumns(result.columns);
                    setItems(result.data);
                }
            }
        }
    }, [studyData, sitesStatusesToExclude, siteScores, reportData]);

    const rowActions = (item: any): IActionItemV2[] => [
        {
            label: 'cm:viewSiteDashboard',
            icon: ['far', 'eye'],
            onClick: () => {
                NavigationService.setViewLegacySiteDashboard();
                navigate(AllRoutes.path('cm-site-dashboard') as string, {
                    state: {
                        skipSelectDate: true,
                        showDashboard: true,
                        siteId: item.id as string | null | undefined,
                    },
                });
            },
            enabled: sitesStatusesToExclude?.indexOf(item.status) < 0 ?? true,
        },
    ];

    if (!studyData) return <h3>No data available</h3>;

    const exportFileName = `${studyData?.study?.Fields?.[
        'Study.number'
    ]}_RiskOverview_Dataset-${stringDateWithoutTimeFromUtc(
        studyData.dataCutDate,
        'DD-MMM-YYYY',
    )}_${stringDateWithoutTimeFromCurrentUtcDate()}`;

    const mapColumns = (inputColumns: RiskOverviewColumn[]) =>
        inputColumns
            .filter((x) => x.show === true)
            .map(
                (column) =>
                    ({
                        title: column.text || column.title,
                        field: column.field === 'siteActiveDate' ? `_${column.field}` : column.field,
                    }) as ExportExcelComponentInputColumn,
            );

    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
        mapColumns,
    };

    return (
        <>
            {(inactive || !items.length) && (
                <p className="warning-message">
                    {inactive ? t('cm:noOverviewTablesSelected') : t('cm:noRiskOverviewColumns')}
                </p>
            )}
            <ExpandableGridV2
                title={t('cm:riskOverview')}
                customKey="study-summary-grid"
                className={cn({ 'grid-inactive': inactive || !items.length })}
                id="studySummaryGrid"
                items={!inactive && items.length ? items : []}
                groupable
                scrollable="scrollable"
                reorderable
                resizable
                pageable={false}
                hideCheckbox
                hideExpand
                gridViewOptions={gridViewOptions}
                rowActions={rowActions}
                switchMenu={switchMenu}
                columns={!inactive && items.length ? columns : []}
                exportConfig={exportConfig}
            />
        </>
    );
};

export default RiskOverviewScrollableGrid;
